import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  type MouseEvent,
} from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { Button, Input, Skeleton } from "antd";

import { Refresh2, Heart } from "iconsax-react";
import { DataTypeVerbete, useSinalario } from "../../../hooks/sinalario";
import { useAuth } from "../../../hooks/auth";
import { favoritesService } from "../../../services/favoritesService";
import {
  filterVerbetesByCategory,
  getDictionaryCategory,
  isDictionaryCategoryId,
} from "../../../services/dictionaryCategories";
import MobileLayout from "../../../components/MobileLayout";

import {
  VerbeteCard,
  SearchWrapper,
  SearchContent,
  VerbeteListGrid,
  ListPageBody,
} from "./styles";

/** Detecta se a palavra é variante (termina com número, ex: Bacalhau2) e retorna o número se houver */
function getVariantInfo(palavra: string): {
  isVariant: boolean;
  variantNumber: number | null;
} {
  if (!palavra?.trim()) return { isVariant: false, variantNumber: null };
  const re = /^(.+?)(\d+)$/;
  const match = re.exec(palavra.trim());
  if (!match) return { isVariant: false, variantNumber: null };
  const num = Number.parseInt(match[2], 10);
  return { isVariant: num > 0, variantNumber: num > 0 ? num : null };
}

function SinalarioList() {
  const [busca, setBusca] = useState("");
  const [data, setData] = useState<DataTypeVerbete[]>([]);
  const [favorites, setFavorites] = useState<string[]>([]);
  const { user } = useAuth();
  const { getVerbete } = useSinalario();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const categoryParam = searchParams.get("category");
  const categoryId = isDictionaryCategoryId(categoryParam)
    ? categoryParam
    : null;
  const categoryMeta = categoryId
    ? getDictionaryCategory(categoryId)
    : undefined;

  const initialLetter =
    (location.state as { letter?: string } | null)?.letter || null;

  useEffect(() => {
    if (!initialLetter && !categoryId) {
      navigate("/sinalario", { replace: true });
    }
  }, [initialLetter, categoryId, navigate]);

  const [selectedLetter] = useState<string | null>(initialLetter);
  const [loading, setLoading] = useState(true);

  const userId = user?.uid;

  const handleGetVerbetes = useCallback(async () => {
    await Swal.fire({
      title: "Carregando...",
      showConfirmButton: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: async () => {
        Swal.showLoading();
        try {
          setLoading(true);
          const verbetes = await getVerbete();
          setData(verbetes);

          if (userId) {
            const favs = await favoritesService.getFavorites(userId);
            setFavorites(favs.map((f) => f.uid || f.palavra));
          }
        } catch (error) {
          console.error("Failed to fetch verbetes:", error);
        } finally {
          setLoading(false);
          Swal.close();
        }
      },
    });
  }, [getVerbete, userId]);

  async function handleToggleFavorite(e: MouseEvent, verbete: DataTypeVerbete) {
    e.stopPropagation();

    if (!userId) {
      Swal.fire({
        icon: "info",
        title: "Login necessário",
        text: "Você precisa estar logado para favoritar verbetes.",
        confirmButtonColor: "var(--color-primary)",
      });
      return;
    }

    const success = await favoritesService.toggleFavorite(
      userId,
      verbete as any
    );

    if (success) {
      setFavorites((prev: string[]) => [
        ...prev,
        verbete.uid || verbete.palavra,
      ]);
    } else {
      setFavorites((prev: string[]) =>
        prev.filter((id: string) => id !== (verbete.uid || verbete.palavra))
      );
    }
  }

  useEffect(() => {
    handleGetVerbetes();
  }, [handleGetVerbetes]);

  function handleViewDetails(record: DataTypeVerbete) {
    navigate(`/VerbeteDetails`, {
      state: record,
    });
  }

  function handleDactylology(e: MouseEvent, record: DataTypeVerbete) {
    e.stopPropagation();
    navigate(`/dactylology`, {
      state: { word: record.palavra, imageUri: record.image },
    });
  }

  const scopedData = useMemo(() => {
    if (categoryId) return filterVerbetesByCategory(data, categoryId);
    return data;
  }, [data, categoryId]);

  const verbWithSearch = scopedData.filter(
    (current: DataTypeVerbete): boolean => {
      const matchesBusca =
        current.palavra?.toUpperCase().includes(busca.toLocaleUpperCase()) ||
        current.descricao?.toUpperCase().includes(busca.toLocaleUpperCase());

      const matchesLetter =
        categoryId || !selectedLetter
          ? true
          : current.letra?.toUpperCase() === selectedLetter.toUpperCase();

      return matchesBusca && matchesLetter;
    }
  );

  const headerTitle = categoryMeta
    ? categoryMeta.label
    : `Palavras com ${selectedLetter || ""}`;

  return (
    <MobileLayout headerTitle={headerTitle} headerType="private">
      <SearchWrapper>
        <SearchContent>
          <div style={{ display: "flex", gap: "12px", marginBottom: "0px" }}>
            <Input
              placeholder="Buscar por palavra..."
              value={busca}
              onChange={(e: any) => setBusca(e.target.value)}
              style={{
                height: "48px",
                borderRadius: "12px",
                background: "#f1f5f9",
                border: "none",
              }}
              prefix={<Refresh2 size={18} color="#4614BB" />}
            />
          </div>
        </SearchContent>
      </SearchWrapper>

      <ListPageBody>
        <p style={{ color: "#64748B", marginBottom: "12px" }}>
          {verbWithSearch.length} verbetes encontrados
        </p>

        <VerbeteListGrid>
          {loading &&
            ["sk-a", "sk-b", "sk-c", "sk-d", "sk-e"].map((skKey) => (
              <Skeleton
                key={skKey}
                active
                avatar
                paragraph={{ rows: 2 }}
                style={{
                  background: "#fff",
                  padding: "16px",
                  borderRadius: "20px",
                }}
              />
            ))}

          {!loading &&
            verbWithSearch.length > 0 &&
            verbWithSearch.map((verbete: DataTypeVerbete) => {
              const variant = getVariantInfo(verbete.palavra || "");
              const isFav = favorites.includes(verbete.uid || verbete.palavra);

              return (
                <VerbeteCard
                  key={verbete.uid || verbete.palavra}
                  onClick={() => handleViewDetails(verbete)}
                >
                  <div className="card-main">
                    <div className="media-box">
                      {verbete.image ? (
                        <img src={verbete.image} alt={verbete.palavra} />
                      ) : null}
                      {!verbete.image && verbete.video ? (
                        <video
                          src={verbete.video}
                          muted
                          playsInline
                          preload="metadata"
                        />
                      ) : null}
                      {!verbete.image && !verbete.video ? (
                        <div className="letter-fallback">{verbete.letra}</div>
                      ) : null}
                    </div>
                    <div className="card-body">
                      <div className="info">
                        <h3>{verbete.palavra}</h3>
                        {variant.isVariant && (
                          <span className="variant-tag">
                            Sinal {variant.variantNumber}
                          </span>
                        )}
                        <p>{verbete.descricao}</p>
                      </div>
                      <div className="favorite-action">
                        <Heart
                          size={22}
                          variant={isFav ? "Bold" : "Linear"}
                          color={isFav ? "#EF4444" : "#4614BB"}
                          onClick={(e) => handleToggleFavorite(e, verbete)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="card-footer">
                    <Button
                      type="primary"
                      className="dactylology-btn"
                      onClick={(e) => handleDactylology(e, verbete)}
                    >
                      Datilologia
                    </Button>
                  </div>
                </VerbeteCard>
              );
            })}

          {!loading && verbWithSearch.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <p style={{ color: "#64748B" }}>
                {categoryId
                  ? "Nenhum sinal encontrado nesta categoria."
                  : "Nenhum sinal encontrado para esta busca ou letra."}
              </p>
            </div>
          )}
        </VerbeteListGrid>
      </ListPageBody>
    </MobileLayout>
  );
}

export default SinalarioList;
